/**
 * Whether an action's cost lets it go ahead -- ONE answer for every Generate button.
 *
 * Three states, because there are three situations and they need different words:
 *
 * - `affordable`: go ahead.
 * - `insufficient`: the balance is KNOWN and smaller than the KNOWN cost.
 * - `unavailable`: the cost could not be read (the server is unreachable, or
 *   answered with something that is not the API -- a Google 503 page when the
 *   project's billing is suspended). The action is blocked, because nobody can
 *   say what it would charge, but it is NOT "insufficient tokens".
 *
 * This used to be a single `hasEnough` boolean. An unreachable server set it to
 * false, and every button read false as "Insufficient Tokens" -- so on
 * 2026-10-08, with Cloud Run down, a funded account was told it had no money,
 * next to a raw "Unexpected token '<'" parse error.
 *
 * An UNKNOWN balance (`null`, see TokenBalanceContext) never blocks by itself:
 * the server is the authority on whether the account can pay, and answers 402
 * when it cannot (which opens the InsufficientTokensModal).
 */

export type CostGateStatus = 'affordable' | 'insufficient' | 'unavailable';

/** The slice of the app's `t` (hooks/useTranslation) this module needs. */
type Translate = (key: string, params: { defaultValue: string }) => string;

/** What a cost card reports to the form that owns the action. */
export interface CostGate {
  /** Tokens the action will reserve (0 when it is free or the cost is unknown). */
  tokens: number;
  status: CostGateStatus;
}

/** A form's gate before any cost card has answered: nothing is blocked yet. */
export const COST_GATE_OPEN: CostGateStatus = 'affordable';

/** The gate for a KNOWN cost against a balance that may be unknown (`null`). */
export function costGateStatus(cost: number, balance: number | null): CostGateStatus {
  if (cost <= 0 || balance === null) return 'affordable';
  return balance >= cost ? 'affordable' : 'insufficient';
}

/** The gate when the cost itself could not be read. */
export const COST_GATE_UNAVAILABLE: CostGate = { tokens: 0, status: 'unavailable' };

/**
 * The words a blocked action button shows, or null when it is not blocked.
 */
export function costGateBlockLabel(status: CostGateStatus, t: Translate): string | null {
  if (status === 'insufficient') {
    return t('tokens.insufficientTokens', { defaultValue: 'Insufficient Tokens' });
  }
  if (status === 'unavailable') {
    return t('tokens.costUnavailable', { defaultValue: 'Cost unavailable' });
  }
  return null;
}
